import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { useEventCallback } from '../../hooks/useEventCallback';
import { Icon, IconType } from '../../misc/Icon/Icon';
import { prepareProps } from '../../utils/propUtils';
import { SidebarWithBackdrop } from '../SidebarWithBackdrop/SidebarWithBackdrop';
import styles from './NavigationSidebar.module.css';

export type NavigationItem = {
  iconName: string;
  iconType: IconType;
  label: string;
  to: NavLinkProps['to'];
};

export type NavigationSidebarProps = {
  navigationItems: Array<NavigationItem>;
  opened: boolean;
  /**
   * this is called when something occured which wants to close the sidebar
   */
  onCloseSidebar: () => void;
  onLogoutClick: () => void;
  onOpenUserProfile: () => void;
  'data-testid'?: string;
};

export const NavigationSidebar: FunctionComponent<NavigationSidebarProps> =
  React.memo((props) => {
    const {
      navigationItems,
      opened,
      'data-testid': testid,
      onCloseSidebar,
      onLogoutClick,
      onOpenUserProfile
    } = prepareProps({ props, ignoreChildren: true });

    const { t } = useTranslation('record-it-components');

    const handleOpenUserProfileClick = useEventCallback(() => {
      onCloseSidebar();
      onOpenUserProfile();
    });

    return (
      <SidebarWithBackdrop
        opened={opened}
        onCloseSidebar={onCloseSidebar}
        data-testid={testid}
      >
        <div className={styles.navItemsWrapper}>
          {navigationItems.map((item, index) => (
            <NavLink
              to={item.to}
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className={styles.navItem}
              onClick={onCloseSidebar}
              data-testid="NavigationSidebar.NavItem"
            >
              <Icon
                name={item.iconName}
                type={item.iconType}
                className={styles.navItemIcon}
              ></Icon>
              <span className={styles.navItemText}>{item.label}</span>
            </NavLink>
          ))}
        </div>
        <div className={styles.bottomSection}>
          <button
            type="button"
            className={styles.navItem}
            onClick={handleOpenUserProfileClick}
            data-testid="NavigationSidebar.UserProfileButton"
          >
            <Icon
              name="user-circle"
              type={IconType.Regular}
              hasTextOnRightSide={true}
            ></Icon>
            <span className={styles.navItemText}>
              {t('pageLayout.NavigationSidebar.userProfile')}
            </span>
          </button>
          <button
            type="button"
            className={styles.navItem}
            onClick={onLogoutClick}
            data-testid="NavigationSidebar.LogoutButton"
          >
            <Icon
              name="sign-out"
              type={IconType.Regular}
              hasTextOnRightSide={true}
            ></Icon>
            <span className={styles.navItemText}>
              {t('pageLayout.NavigationSidebar.logout')}
            </span>
          </button>
        </div>
      </SidebarWithBackdrop>
    );
  });
